import { TableChip } from '../../components/PremiumDesign';
import { SkeletonText } from '@/components';
import {
  formatDateTime,
  getFollowUpStatusChipStyle,
} from '../utils/tasksAndFollowups';
import editIcon from '../../assets/editIcon.png';
import trashIcon from '../../assets/trashIcon.png';
import eyeIcon from '../../assets/eye.png';
import pauseCircleIcon from '../../assets/leads/pauseCircle.png';
import rescheduleIcon from '../../assets/leads/reschedule.png';
import playIcon from '../../assets/leads/play.png';
import pauseButtonIcon from '../../assets/leads/pauseButton.png';

const sk = (width, extra = '') => (
  <SkeletonText width={width} height="h-4" className={extra} />
);
const chipSk = (
  <SkeletonText width="w-20" height="h-6" className="rounded-full" />
);
const actionSk = <SkeletonText width="w-24" height="h-8" className="ml-auto" />;

function templateLabel(d) {
  return (
    d?.template_name ||
    d?.template?.name ||
    (d?.message_type === 'message'
      ? d?.standard_message_body || 'Standard Message'
      : '-')
  );
}

function TaskActions({ item, onComplete, onEdit, onDelete, completePending }) {
  const taskObj = item.data;
  const isCompleted =
    !!taskObj?.completed_at || taskObj?.status === 'completed';
  return (
    <div className="flex items-center justify-end gap-1">
      <button
        type="button"
        onClick={() => {
          if (item.contact?.id && taskObj?.id && !isCompleted) {
            onComplete({ contactId: item.contact.id, taskId: taskObj.id });
          }
        }}
        disabled={completePending || isCompleted}
        className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all focus:outline-none shrink-0 ${
          isCompleted
            ? 'bg-[#50AB3D] border-[#50AB3D] text-white cursor-default'
            : 'bg-white border-[#868686] text-transparent hover:border-[#50AB3D] cursor-pointer'
        }`}
        title={isCompleted ? 'Completed' : 'Mark as completed'}
      >
        {isCompleted && (
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </button>
      <button
        type="button"
        onClick={() => onEdit(item)}
        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#FAF6E6] focus:outline-none shrink-0 cursor-pointer"
        title="Edit task"
      >
        <img
          src={editIcon}
          alt="Edit"
          className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
        />
      </button>
      <button
        type="button"
        onClick={() => onDelete(item)}
        className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[#FCEAEC] focus:outline-none shrink-0 cursor-pointer"
        title="Delete task"
      >
        <img
          src={trashIcon}
          alt="Delete"
          className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
        />
      </button>
    </div>
  );
}

function FollowUpActions({ item, onAction, onView, onDelete, busy }) {
  const taskObj = item.data;
  const s = String(taskObj?.status || '')
    .toLowerCase()
    .trim();
  const id = { contactId: item.contact?.id, followUpId: taskObj?.id };
  let control = null;
  if (s === 'scheduled') {
    control = (
      <button
        type="button"
        disabled={busy.stop}
        onClick={() => onAction({ type: 'stop', ...id })}
        className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-yellow-500/10 focus:outline-none disabled:opacity-50"
        title="Stop Follow-up"
      >
        <img
          src={pauseCircleIcon}
          alt="Stop"
          className="w-5 h-5 object-contain"
        />
      </button>
    );
  } else if (s === 'stopped' || s === 'failed') {
    control = (
      <button
        type="button"
        disabled={busy.reschedule}
        onClick={() => onAction({ type: 'reschedule', ...id })}
        className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-purple-500/10 focus:outline-none disabled:opacity-50"
        title="Reschedule Follow-up"
      >
        <img
          src={rescheduleIcon}
          alt="Reschedule"
          className="w-5 h-5 object-contain"
        />
      </button>
    );
  } else if (s === 'paused') {
    control = (
      <button
        type="button"
        disabled={busy.resume}
        onClick={() => onAction({ type: 'resume', ...id })}
        className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-green-500/10 focus:outline-none disabled:opacity-50"
        title="Start Follow-up"
      >
        <img src={playIcon} alt="Start" className="w-5 h-5 object-contain" />
      </button>
    );
  } else if (s === 'active' || s === 'running') {
    control = (
      <button
        type="button"
        disabled={busy.pause}
        onClick={() => onAction({ type: 'pause', ...id })}
        className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-green-500/10 focus:outline-none disabled:opacity-50"
        title="Pause Follow-up"
      >
        <img
          src={pauseButtonIcon}
          alt="Pause"
          className="w-5 h-5 object-contain"
        />
      </button>
    );
  }
  return (
    <div className="flex items-center justify-end gap-1">
      {control}
      <button
        type="button"
        onClick={() => onView(item)}
        className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-blue-500/10 focus:outline-none"
        title="View Follow-up"
      >
        <img src={eyeIcon} alt="View" className="w-5 h-5 object-contain" />
      </button>
      <button
        type="button"
        onClick={() => onDelete(item)}
        className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg hover:bg-red-500/10 focus:outline-none"
        title="Delete follow-up"
      >
        <img
          src={trashIcon}
          alt="Delete"
          className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
        />
      </button>
    </div>
  );
}

export function buildTasksFollowupsColumns(ctx) {
  const sr = {
    id: 'sr',
    label: 'Sr No.',
    defaultWidth: 80,
    minWidth: 64,
    resizable: false,
    skeleton: sk('w-8'),
    cell: (_row, index) => (ctx.page - 1) * 10 + index + 1,
  };
  const action = {
    id: 'action',
    label: 'Action',
    defaultWidth: 150,
    minWidth: 120,
    resizable: false,
    align: 'center',
    stopPropagation: true,
    skeleton: actionSk,
    cell: (item) =>
      ctx.initialType === 'task' ? (
        <TaskActions
          item={item}
          onComplete={ctx.onComplete}
          onEdit={ctx.onEditTask}
          onDelete={ctx.onDeleteTask}
          completePending={ctx.completePending}
        />
      ) : (
        <FollowUpActions
          item={item}
          onAction={ctx.onAction}
          onView={ctx.onViewFollowUp}
          onDelete={ctx.onDeleteFollowUp}
          busy={ctx.busy}
        />
      ),
  };

  if (ctx.initialType === 'task') {
    return [
      sr,
      {
        id: 'customer',
        label: 'Customer',
        defaultWidth: 160,
        minWidth: 110,
        resizable: true,
        skeleton: sk('w-28'),
        cell: (item) => (
          <span className="truncate block font-medium">
            {item.contact?.name || '-'}
          </span>
        ),
      },
      {
        id: 'phone',
        label: 'Phone',
        defaultWidth: 140,
        minWidth: 100,
        resizable: true,
        skeleton: sk('w-24'),
        cell: (item) => item.contact?.phone || '-',
      },
      {
        id: 'title',
        label: 'Task Title',
        defaultWidth: 200,
        minWidth: 140,
        resizable: true,
        skeleton: sk('w-40'),
        cell: (item) => (
          <span className="truncate block">{item.data?.title || '-'}</span>
        ),
      },
      {
        id: 'assigned',
        label: 'Assigned To',
        defaultWidth: 150,
        minWidth: 110,
        resizable: true,
        skeleton: sk('w-24'),
        cell: (item) =>
          item.data?.assigned_to_employee_name ||
          item.data?.assigned_to_name ||
          'Unassigned',
      },
      {
        id: 'due',
        label: 'Due Date',
        defaultWidth: 160,
        minWidth: 120,
        resizable: true,
        skeleton: sk('w-28'),
        cell: (item) => formatDateTime(item.data?.due_at),
      },
      {
        id: 'completed',
        label: 'Completed Date',
        defaultWidth: 170,
        minWidth: 130,
        resizable: true,
        skeleton: sk('w-28'),
        cell: (item) =>
          item.data?.completed_at
            ? formatDateTime(item.data.completed_at)
            : '-',
      },
      {
        id: 'status',
        label: 'Status',
        defaultWidth: 130,
        minWidth: 100,
        resizable: true,
        skeleton: chipSk,
        cell: (item) => {
          const done =
            !!item.data?.completed_at || item.data?.status === 'completed';
          return (
            <TableChip tone={done ? 'success' : 'warning'}>
              {done ? 'Completed' : 'Pending'}
            </TableChip>
          );
        },
      },
      action,
    ];
  }

  return [
    sr,
    {
      id: 'template',
      label: 'Template Name',
      defaultWidth: 200,
      minWidth: 140,
      resizable: true,
      skeleton: sk('w-40'),
      cell: (item) => (
        <span className="truncate block font-medium">
          {templateLabel(item.data)}
        </span>
      ),
    },
    {
      id: 'customer',
      label: 'Customer',
      defaultWidth: 150,
      minWidth: 110,
      resizable: true,
      skeleton: sk('w-28'),
      cell: (item) => (
        <span className="truncate block font-medium">
          {item.contact?.name || '-'}
        </span>
      ),
    },
    {
      id: 'type',
      label: 'Follow-Up Type',
      defaultWidth: 140,
      minWidth: 110,
      resizable: true,
      skeleton: sk('w-20'),
      cell: (item) =>
        item.data?.follow_up_type === 'scheduled'
          ? 'Scheduled'
          : item.data?.follow_up_type === 'interval'
            ? 'Interval'
            : item.data?.follow_up_type || '-',
    },
    {
      id: 'message',
      label: 'Message Type',
      defaultWidth: 140,
      minWidth: 110,
      resizable: true,
      skeleton: sk('w-20'),
      cell: (item) =>
        item.data?.message_type === 'template'
          ? 'Template'
          : item.data?.message_type === 'message'
            ? 'Message'
            : item.data?.message_type || '-',
    },
    {
      id: 'attempts',
      label: 'Attempts',
      defaultWidth: 110,
      minWidth: 80,
      resizable: true,
      skeleton: sk('w-12'),
      cell: (item) =>
        `${item.data?.completed_attempts || item.data?.current_attempts || 0}/${item.data?.max_attempts > 0 ? item.data.max_attempts : '∞'}`,
    },
    {
      id: 'next',
      label: 'Next Execution',
      defaultWidth: 160,
      minWidth: 120,
      resizable: true,
      skeleton: sk('w-28'),
      cell: (item) =>
        item.data?.follow_up_type === 'interval' &&
        (item.data?.next_execution_time || item.data?.next_execution_at)
          ? formatDateTime(
              item.data.next_execution_time || item.data.next_execution_at,
            )
          : '-',
    },
    {
      id: 'scheduled',
      label: 'Scheduled For',
      defaultWidth: 160,
      minWidth: 120,
      resizable: true,
      skeleton: sk('w-28'),
      cell: (item) =>
        item.data?.follow_up_type === 'scheduled' &&
        (item.data?.scheduled_date_time || item.data?.scheduled_for)
          ? formatDateTime(
              item.data.scheduled_date_time || item.data.scheduled_for,
            )
          : '-',
    },
    {
      id: 'status',
      label: 'Status',
      defaultWidth: 130,
      minWidth: 100,
      resizable: true,
      skeleton: chipSk,
      cell: (item) => {
        const status = item.data?.status || 'active';
        const label = status.charAt(0).toUpperCase() + status.slice(1);
        return (
          <TableChip {...getFollowUpStatusChipStyle(status)}>{label}</TableChip>
        );
      },
    },
    action,
  ];
}
